import React, {useEffect, useMemo, useState} from 'react';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { MultiSelect } from 'primereact/multiselect';
import * as Constants from "../../constants";
import B2BApiService from "../../B2bApiService";
import WriteAccessGuard from '../../components/reusableElementsComponents/WriteAccessGuard';
import { toast } from 'react-toastify';
import { t } from '../../content';

export default function AssignTeamsComp() {
  const b2bServiceObj = useMemo(() => {
    return new B2BApiService();
  }, []);

  const reduxCompanyUsers = useSelector(state => state.company.users || []);

  const [assignTeams, setAssignTeams] = useState({});
  const [changedRows, setChangedRows] = useState({});
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);

  const handleMultiSelectTeams = (e, userId) => {
    // Only teams with unique hashes should be selected
    const uniqueTeams = Array.from(new Set(e.value.map(team => team.hash)))
      .map(hash => e.value.find(team => team.hash === hash));

    setAssignTeams((prevSelectedRoles) => ({
      ...prevSelectedRoles,
      [userId]: uniqueTeams,
    }));
    setChangedRows((prevChangedRows) => ({
      ...prevChangedRows,
      [userId]: true,
    }));
  };


  const assignedTeamsLabel = (count) =>
    t(count === 1 ? 'iam.teamsAssignedOne' : 'iam.teamsAssignedMany');

  const multiSelectTeamsTemplate = (rowData) => {
    const selectedCount = (assignTeams[rowData.id] || []).length;
    return (
    <MultiSelect
      key={rowData?.id + '-teams'}
      value={assignTeams[rowData.id] || []}
      options={teams}
      optionLabel='title'
      onChange={(e) => handleMultiSelectTeams(e, rowData.id)}
      placeholder={t('iam.selectTeams')}
      selectedItemsLabel={assignedTeamsLabel(selectedCount)}
      filter
      maxSelectedLabels={0}
    />
    );
  };

  const saveAssignedTeams = (userId) => {
    let teamHashes = assignTeams[userId].map(assignedTeam => {
      const item = teams.find(item => item.hash === assignedTeam.hash);
      return item ? item.hash : null;
    });

    b2bServiceObj.UpdateUserAccessToTeams(userId, teamHashes,(res) => {
      if(res.httpStatus === 200){
        setChangedRows((prevChangedRows) => ({
          ...prevChangedRows,
          [userId]: false,
        }));
        toast.success(
          t('iam.toasts.assignTeamsSuccess'),
          {
            containerId: "IAMToast",
          }
        );
      } else {
        toast.error(
          t('iam.toasts.assignTeamsError'),
          {
            containerId: "IAMToast",
          }
        );
      }
    })
  }

  const actionTemplate = (rowData) => (
    <WriteAccessGuard>
      <button 
        className={changedRows[rowData.id] ? 'mazhrBtn default-green color-blink' : 'mazhrBtn default-green'} 
        onClick={() => saveAssignedTeams(rowData.id)}
        >{t('common.actions.saveShort')}</button>
    </WriteAccessGuard>
  );

  const columns = [
    { field: 'name', header: t('common.labels.name') },
    { field: 'email', header: t('common.fields.email') },
    { field: 'role_id', header: t('common.labels.role'), body: (rowData) => Constants.getUserRoleStringByRoleId(rowData.role_id) },
    { field: 'multiSelectTeams', header: t('iam.assignedTeams'), body: multiSelectTeamsTemplate },
    { field: 'action', header: t('common.labels.actions'), body: actionTemplate },
  ];

  useEffect(() => {
    b2bServiceObj.LoadTeamsWithIdAndTitle((data) => {
      setTeams(data);
    })
  }, [b2bServiceObj]); // Empty dependency array ensures this effect runs only once on component mount

  useEffect(() => {
    // Check if reduxCompanyUsers is not empty. If not, use it to set users state. Otherwise, fetch from server.
    if (reduxCompanyUsers && reduxCompanyUsers.length > 0 && reduxCompanyUsers[0].hasOwnProperty('id')) {
      setUsers(reduxCompanyUsers.filter((user) => [Constants.USER_ROLE_TEAMLEAD, Constants.USER_ROLE_MANAGER].includes(user.role_id)));   
    }
  }, [b2bServiceObj, reduxCompanyUsers]);

  useEffect(() => {
    // Initialize assignTeams state with the team titles for each user
    const initialAssignTeams = {};
    users.forEach(user => {
      initialAssignTeams[user.id] = user.team_hashes.map(hash => {
        const team = teams.find(t => t.hash === hash);
        return team ? team : null;
      }).filter(team => team !== null);
    });
    setAssignTeams(initialAssignTeams);
  }, [users, teams]);
  
  return (
    <div className="user-list-wrapper-div">
      <div className="margin-left-div">
        <div className="user-list-inner-div">
          <DataTable value={users} paginator rows={10} rowsPerPageOptions={[10, 15, 20]} 
            emptyMessage={t('iam.emptyAssignTeams')}>
            {columns.map((column, i) => (
              <Column key={i} field={column.field} header={column.header} body={column.body} />
            ))}
          </DataTable>
        </div>
      </div>
    </div>
  );
}
